<template>
  <div ref="modal" class="dtf-first-time-customer-modal modal" role="dialog">
    <div class="dtf-first-time-customer-modal__modal-box modal-dialog">
      <div class="dtf-first-time-customer-modal__modal-body">
        <button
          class="dtf-first-time-customer-modal__modal-button-close"
          type="button"
          @click="closeModal"
        >
          <UseSvg icon="modal-close-big" />
        </button>
        <div class="dtf-first-time-customer-modal__feature-image">
          <div class="dtf-first-time-customer-modal__feature-bg"></div>
          <img
            src="https://jiffy.imgix.net/gen/landing-dtf-1.jpeg?auto=format&w=400&h=400&q=80&dpr=2"
            alt="Sample image 1"
            class="dtf-first-time-customer-modal__image dtf-first-time-customer-modal__image--dtf-first"
          />
          <img
            src="https://jiffy.imgix.net/gen/landing-dtf-2.jpg?auto=format&w=400&h=400&q=80&dpr=2"
            alt="Sample image 2"
            class="dtf-first-time-customer-modal__image dtf-first-time-customer-modal__image--dtf-main"
          />
          <img
            src="https://jiffy.imgix.net/gen/landing-dtf-3.jpg?auto=format&w=400&h=400&q=80&dpr=2"
            alt="Sample image 3"
            class="dtf-first-time-customer-modal__image dtf-first-time-customer-modal__image--dtf-last"
          />
        </div>
        <div class="dtf-first-time-customer-modal__alert">
          <UseSvg icon="jiffy-transfers-inline" class="dtf-first-time-customer-modal__logo" />
          <div class="dtf-first-time-customer-modal__promo-text">
            <div class="dtf-first-time-customer-modal__promo-text--medium">Get</div>
            <div class="dtf-first-time-customer-modal__promo-text--large">20% OFF</div>
            <div class="dtf-first-time-customer-modal__promo-text--small">
              your first Jiffy Transfer order!
            </div>
          </div>
          <div class="dtf-first-time-customer-modal__promo-code">
            Code:
            <span
              id="js-promo-code"
              class="dtf-first-time-customer-modal__promo-code--bold"
              @click="copyPromoCode"
            >
              DTF20
            </span>
          </div>
          <div class="dtf-first-time-customer-modal__text">Use this code at checkout</div>
          <a href="/jiffytransfers" class="dtf-first-time-customer-modal__text-link">
            What is
            <span class="dtf-first-time-customer-modal__text-link--underlined">
              Jiffy Transfers?
            </span>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { track } from "jiffy-analytics";
import { api } from "jiffy-api";
import { writeCookie } from "common/utils/cookie";
import { track as rudderstackTrack } from "common/utils/rudderstack/tracker";
import { asyncRetryIfGatewayError } from "common/utils/retries";
import { mapState } from "pinia";
import UseSvg from "../vue-cart/UseSvg.vue";
import { useDtfStore } from "./store";

export default {
  name: "DtfFirstTimeCustomerModal",
  components: { UseSvg },
  props: {
    orderNumber: {
      default: undefined,
      type: String
    },
    showPromoModalOverride: {
      default: undefined,
      type: Boolean
    }
  },
  emits: ["submit", "close"],
  computed: {
    ...mapState(useDtfStore, ["showPromoModal"])
  },
  data() {
    return {
      lastScrollPosition: null,
      scrollTimer: null
    };
  },
  async mounted() {
    const payload = {};
    if (this.orderNumber) {
      payload.orderNumber = this.orderNumber;
    }

    await this.tryShowingPromoModal();
  },
  methods: {
    async copyPromoCode() {
      try {
        const promoCode = "DTF20";
        await navigator.clipboard.writeText(promoCode);
      } catch (err) {
        // do nothing
      }
    },
    async tryShowingPromoModal() {
      const shouldShowModal = this.showPromoModalOverride || this.showPromoModal;
      if (shouldShowModal) {
        this.showModal();
        return;
      }

      try {
        await asyncRetryIfGatewayError(async () => {
          const { show: eligibleForPromo } = await api.dtf.firstTimePromoModal();
          if (eligibleForPromo) this.showModal();
        });
      } catch (error) {
        window.Honeybadger?.notify("DTF promo modal error", {
          params: { error: this.parseError(error) }
        });
      }
    },
    parseError(error) {
      const errorObj = JSON.parse(JSON.stringify(error));
      if (Object.keys(errorObj).length === 0 && errorObj.constructor === Object) {
        return error.message;
      }

      return errorObj;
    },
    markModalAsShown() {
      writeCookie("dtf-first-time-customer-promo-shown", true, 86400);
    },
    showModal() {
      track("dtf_promo_modal_shown");
      rudderstackTrack("Promotion Viewed", {
        is_app: Boolean(window.MOBILE_APP),
        location: "dtf_first_time_customer_modal",
        platform: window.MOBILE_APP_PLATFORM || "web",
        text: "20% OFF YOUR FIRST JIFFY TRANSFER ORDER!",
        type: "promotion_modal",
        url: window.location.href
      });
      this.markModalAsShown();
      const $modal = $(this.$refs.modal);
      $modal.modal("show").on("hidden.bs.modal", () => {
        $modal.off("hidden.bs.modal");
        if (!this.closed) {
          this.closeModal();
        }
      });
    },
    closeModal() {
      track("dtf_promo_modal_closed");
      $(this.$refs.modal).modal("hide");
      this.closed = true;
      this.$emit("close");
    }
  }
};
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/mixin.scss";
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/button/mixin.scss";

.dtf-first-time-customer-modal {
  &__modal-box {
    margin: auto;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    width: 360px;
    height: 640px;

    @include only-mobile {
      .modal.in & {
        transform: translate(0, 0) scale(0.8);
      }
    }

    @include with-tablet {
      width: 790px;
      height: 580px;
    }
  }

  &__modal-body {
    display: flex;
    flex-direction: column;
    height: 100%;

    @include with-tablet {
      flex-direction: row;
      height: auto;
    }
  }

  &__modal-button-close {
    @include clear-button;
    @include svg-wrapper;

    position: absolute;
    top: 0;
    right: 0;
    width: 48px;
    height: 48px;
    padding: 16px;
    z-index: 1;
  }

  &__feature-image {
    flex-grow: 0;
    flex-basis: 256px;
    position: relative;
    overflow: hidden;
    min-height: 256px;
    background-color: $crafterpay-gray-modern-100;
    border-top-left-radius: 3px;
    border-top-right-radius: 3px;

    @include with-tablet {
      flex: 1 1 50%;
      height: 580px;
      border-top-right-radius: 3px;
      border-bottom-left-radius: 3px;
    }
  }

  &__feature-bg {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    background-image: url(https://jiffy.imgix.net/gen/splash1.png);
    opacity: 0.5;
    z-index: 0;
    background-repeat: no-repeat;
    background-position: center center;
    mix-blend-mode: multiply;
    transform: rotate(35deg);
    background-size: 90%;

    @include with-tablet {
      left: -150px;
      top: -150px;
      right: -150px;
      bottom: -150px;
    }
  }

  &__image {
    width: 95px;
    height: 101px;
    border-radius: 12px;
    border: 6px solid $white;
    box-shadow: 0 12px 40px 0 rgba(0, 0, 0, 0.15);
    object-fit: cover;
    display: block;
    position: absolute;

    &--dtf-first {
      top: 24px;
      left: 47px;
      object-position: 12%;
    }

    &--dtf-main {
      z-index: 10;
      display: block;
      width: 132px;
      height: 140px;
      top: 61px;
      left: 119px;
    }

    &--dtf-last {
      bottom: 24px;
      right: 49px;
    }

    @include with-tablet {
      width: 151px;
      height: 161px;

      &--dtf-first {
        left: 18px;
        top: 82px;
      }

      &--dtf-main {
        top: 170px;
        left: 95px;
        width: 209px;
        height: 224px;
      }

      &--dtf-last {
        right: 24px;
        bottom: 82px;
      }
    }
  }

  &__alert {
    flex: 1 1 50%;
    background-color: $white;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border-bottom-left-radius: 3px;
    border-bottom-right-radius: 3px;

    @include with-tablet {
      height: 580px;
      border-bottom-left-radius: 0;
      border-top-right-radius: 3px;
    }
  }

  &__logo {
    width: 176px;
    height: 19px;

    @include with-tablet {
      width: 211px;
      height: 23px;
    }
  }

  &__promo-text {
    font-family: sans-serif;
    font-weight: 700;
    color: $dark-blue2;
    text-align: center;
    line-height: 50px;
    margin-top: 12px;

    &--small {
      font-size: 20px;
    }

    &--medium {
      font-size: 40px;
    }

    &--large {
      font-size: 60px;
    }

    @include with-tablet {
      margin-top: 40px;
    }
  }

  &__promo-code {
    border-radius: 3px;
    border: 2px dashed $bright-pink;
    color: $bright-pink;
    background: $lighter-pink;
    padding: 15px;
    font-size: 29px;
    line-height: 100%;
    margin: 10px auto 10px;

    &--bold {
      font-weight: 700;
      font-family: Arial-black, Arial;
    }

    @include with-tablet {
      margin-top: 20px;
    }
  }

  &__text {
    font-family: sans-serif;
    font-size: 14px;
    line-height: 24px;
  }

  &__text-link {
    font-family: sans-serif;
    margin-top: 8px;
    color: $dark-blue2;
    font-size: 12px;
    line-height: 12px;
    font-weight: 700;

    @include with-tablet {
      margin-top: 20px;
      margin-bottom: 0;
    }

    &:hover {
      text-decoration: none;
    }

    &--underlined,
    &--underlined:hover {
      text-decoration: underline;
    }
  }
}
</style>
